import DutyStatusLog from "../model/DutyStatusLog";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import UserHosRuleSet from "../model/UserHosRuleSet";
import { DriveStore } from "../store/reducers";
import Sync from "../sync/sync";
import StartOfDayDialog from "../ui/framework/startOfDayDialog";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { getRuleSetIdByIndex, hasCustomStartOfDay, hasResetPreviousCycle } from "hos-tools";
import { SecurityService } from "../utils/security";
import { getServerDate } from "../utils/storage/date";
import { UserService } from "../utils/users";
import HosProcess from "./hosProcess";
import TranslationService from "./translation";

export default class ChangeStartOfDayService {
    constructor(
        private readonly userService: UserService,
        private readonly sync: Sync,
        private readonly hosUtils: HosProcess,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly securityService: SecurityService,
        private readonly translatorService: TranslationService,
        private readonly store: DriveStore,
        private readonly currentUser: IUser | null
    ) {}

    /**
     * Offers the user the ability to change the start of their day if certain conditions are met.
     *
     * This method orchestrates the process:
     * 1. Performs initial checks to see if any dialog can be opened.
     * 2. Retrieves the relevant HOS rule set key.
     * 3. Performs rule set specific validations to determine if the start-of-day dialog is applicable.
     * 4. If all conditions are met, it delegates to a helper to manage the dialog interaction.
     * 5. Resolves the promise when the operation is complete or if conditions are not met.
     *
     * @param log - The duty status log used for initial checks and rule set context.
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    offerChangeStartOfDay(log: DutyStatusLog) {
        if (!this.canOpen(log)) {
            return Promise.resolve();
        }
        const validRuleSetKey = this.hosUtils.getValidRuleSet(this.hosUtils.getLatestHosRuleSetIdShortened(true));

        if (!this.isRuleSetValidForStartOfDayChange(validRuleSetKey)) {
            return Promise.resolve();
        }
        return this.handleStartOfDayDialogInteraction(validRuleSetKey);
    }

    /**
     * Determines if the rule set conditions specifically allow for opening the Start of Day Change dialog.
     *
     * @param validRuleSetKey - The calculated valid rule set key.
     * @returns `true` if rule set conditions are met, `false` otherwise.
     */
    private isRuleSetValidForStartOfDayChange(validRuleSetKey: string): boolean {
        const validationResult = this.hosUtils.validateUserHosRuleSet(getRuleSetIdByIndex(validRuleSetKey));

        const hasCustomStart = hasCustomStartOfDay(validRuleSetKey);
        const hasResetCycle = hasResetPreviousCycle(validRuleSetKey);
        const ruleSetNeedsCompletion = !validationResult.isValid && validationResult.ruleSetToComplete;

        return hasCustomStart && hasResetCycle && !ruleSetNeedsCompletion;
    }

    /**
     * Handles the full interaction with the StartOfDayDialog, including opening it,
     * processing user input, and triggering the save operation if needed.
     *
     * @param validRuleSetKey - The rule set key to be used if a change needs to be saved.
     * @returns {Promise<void>} A promise that resolves when the dialog interaction is complete.
     */
    private handleStartOfDayDialogInteraction(validRuleSetKey: string) {
        const startOfDayDialog = new StartOfDayDialog({
            translator: this.translatorService.getTranslator(),
            isUserNightMode: this.store.getState().data.nightMode,
            persistent: true
        });

        const currentStartOfDay = this.dutyStatusLogService.getCurrentRuleSetStartOfDay(this.hosUtils) || "00:00:00";

        return startOfDayDialog
            .open(currentStartOfDay, false)
            .then(startOfDay => {
                if (startOfDay && startOfDay !== currentStartOfDay) {
                    return this.saveRuleSetChange(validRuleSetKey, startOfDay);
                }
                return Promise.resolve();
            })
            .then(() => {
                startOfDayDialog.close();
                return Promise.resolve();
            })
            .catch(e => {
                return Promise.reject(e);
            });
    }

    /**
     * Saves a rule set change for the current user with the specified start of day.
     *
     * @param ruleSetIndex - The index of the rule set to be changed.
     * @param startOfDay - The new start of day time, or null if not applicable.
     * @returns {Promise<void>} A promise that resolves when the rule set change has been saved.
     */
    private saveRuleSetChange(ruleSetIndex: string, startOfDay: string | null) {
        if (this.currentUser && startOfDay) {
            let ruleSet = new UserHosRuleSet({
                id: "",
                hosRuleSet: { id: `HosRuleSet${ ruleSetIndex }` },
                user: { id: this.currentUser.id },
                dateTime: getServerDate().toISOString(),
                startOfDay
            });
            return this.sync.addUserHosRuleSet(this.currentUser, ruleSet);
        }
        return Promise.resolve();
    }

    /**
     * Determines whether the change-start-of-day dialog can be opened.
     *
     * This method checks the following conditions:
     * 1. The log's driver ID must match the current user's ID.
     * 2. The current user must be actively using the UI.
     * 3. The user must have the necessary permissions to use HOS and edit user data.
     *
     * @param log - The duty status log used to validate the conditions.
     * @returns `true` if all conditions are met and the dialog can be opened, `false` otherwise.
     */
    private canOpen(log: DutyStatusLog): boolean {
        if (document.getElementById("select-start-of-day")) {
            return false;
        }
        return log.driver.id === this.currentUser?.id && this.userService.isDriverUsingUI(this.currentUser?.name || "") && this.securityService.can("useHos") && this.securityService.can("editUser");
    }
}
